import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Grid, Input, Space, Typography, message, type TableColumnsType } from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { Permission } from '@aid/shared';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/DataTable';
import { StatusTag } from '../../components/StatusTag';
import { EmptyState } from '../../components/EmptyState';
import { useHasPermission } from '../../auth/useHasPermission';
import { useT } from '../../locales';
import { useDebouncedValue } from '../../lib/useDebouncedValue';
import { paginationProps } from '../../lib/paginacion';
import { serverSortProps, useServerPaginacion } from '../../lib/server-sort';
import type { RoleResponse } from '../../api/types';
import { useDeleteRole, useRolesList, useUpdateRole } from './api/useRoles';
import { RoleActions } from './components/RoleActions';

export function RolesListPage() {
  const navigate = useNavigate();
  const t = useT();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const [search, setSearch] = useState('');
  const searchDebounced = useDebouncedValue(search);
  const { page, pageSize, sort, handleChange, setPage } = useServerPaginacion<RoleResponse>(20);
  const canRead = useHasPermission(Permission.ROLES_READ);
  const canCreate = useHasPermission(Permission.ROLES_CREATE);
  const canUpdate = useHasPermission(Permission.ROLES_UPDATE);
  const canDeactivate = useHasPermission(Permission.ROLES_DELETE);
  const deleteRole = useDeleteRole();
  const updateRole = useUpdateRole();

  const { data, isLoading, error, refetch } = useRolesList({
    limit: pageSize,
    offset: (page - 1) * pageSize,
    search: searchDebounced.trim() || undefined,
    ...sort,
  });

  const handleDeactivate = async (id: string) => {
    try {
      await deleteRole.mutateAsync(id);
      message.success(t('pages.roles.deactivateSuccess'));
    } catch {
      message.error(t('pages.roles.deactivateError'));
    }
  };

  const handleReactivate = async (id: string) => {
    try {
      await updateRole.mutateAsync({ id, body: { isActive: true } });
      message.success(t('pages.roles.reactivateSuccess'));
    } catch {
      message.error(t('pages.roles.reactivateError'));
    }
  };

  if (error) {
    return (
      <EmptyState
        title={t('pages.roles.errorLoad')}
        action={<Button onClick={() => void refetch()}>{t('pages.roles.retryRole')}</Button>}
      />
    );
  }

  const columns: TableColumnsType<RoleResponse> = [
    {
      title: t('pages.roles.name'),
      dataIndex: 'name',
      key: 'name',
      ...serverSortProps('name', sort),
      render: (value: string) => <Typography.Text strong>{value}</Typography.Text>,
    },
    {
      title: t('pages.roles.description'),
      dataIndex: 'description',
      key: 'description',
      ...serverSortProps('description', sort),
      render: (value: string | null) =>
        value ? value : <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: t('pages.roles.status'),
      dataIndex: 'isActive',
      key: 'isActive',
      ...serverSortProps('isActive', sort),
      render: (active: boolean) => (
        <StatusTag tone={active ? 'success' : 'neutral'}>
          {active ? t('pages.roles.active') : t('pages.roles.inactive')}
        </StatusTag>
      ),
    },
    {
      title: t('pages.roles.permissions'),
      key: 'permissions',
      render: (_value: unknown, record: RoleResponse) => (
        <StatusTag tone="info">
          {t('pages.roles.permissionCount', { count: record.permissions.length })}
        </StatusTag>
      ),
    },
    {
      title: t('pages.roles.actions'),
      key: 'actions',
      align: 'right',
      render: (_value: unknown, record: RoleResponse) => (
        <RoleActions
          role={record}
          canRead={canRead}
          canUpdate={canUpdate}
          canDeactivate={canDeactivate}
          loading={deleteRole.isPending || updateRole.isPending}
          onOpen={(role) => navigate(`/roles/${role.id}`)}
          onDeactivate={(role) => void handleDeactivate(role.id)}
          onReactivate={(role) => void handleReactivate(role.id)}
        />
      ),
    },
  ];

  const basePagination = paginationProps(t, {
    page,
    pageSize,
    total: data?.total ?? 0,
  });
  const pagination = {
    ...basePagination,
    simple: isMobile,
    showSizeChanger: !isMobile,
    showTotal: isMobile ? undefined : basePagination.showTotal,
  };
  return (
    <div>
      <PageHeader
        title={t('pages.roles.title')}
        subtitle={t('pages.roles.subtitle')}
        actions={
          canCreate && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/roles/new')}>
              {t('pages.roles.create')}
            </Button>
          )
        }
      />

      <Space
        wrap
        size={[8, 8]}
        style={{ width: '100%', marginBottom: 16, justifyContent: 'space-between' }}
      >
        <Input
          id="roles-search"
          name="search"
          aria-label={t('pages.roles.searchPlaceholder')}
          placeholder={t('pages.roles.searchPlaceholder')}
          prefix={<SearchOutlined />}
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
          style={{ width: 280, maxWidth: '100%' }}
          allowClear
        />
      </Space>

      <DataTable<RoleResponse>
        columns={columns}
        dataSource={data?.data ?? []}
        rowKey="id"
        loading={isLoading}
        pagination={pagination}
        onChange={handleChange}
        locale={{
          emptyText: searchDebounced.trim() ? t('pages.roles.noResults') : t('pages.roles.empty'),
        }}
      />
    </div>
  );
}
